import { SUPER_ADMIN_ID, SYSTEM_USER_ID } from '@shared/constants/user';
import type { AvatarProps } from '@varadise-ltd/cosmology';
import { sum } from 'lodash';

export const hexToRgba = (hex: string, alpha: number): string | null => {
	const validHex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	if (!validHex) {
		return null;
	}

	const [r, g, b] = validHex.slice(1).map((x) => parseInt(x, 16));
	const rgba = `rgba(${r}, ${g}, ${b}, ${alpha})`;

	return rgba;
};

export const getValueFromCssVariable = (val: string) => {
	const style = getComputedStyle(document.body);

	const matches = val.match(/\((.*?)\)/);
	const variable = matches && matches[1];

	return variable ? style.getPropertyValue(variable) : val;
};

export const isHidden = (element: HTMLElement) => {
	const { width, height } = element.getBoundingClientRect();
	return width === 0 && height === 0;
};

/**
 * Returns the avatar color variant name based on a hex color code.
 * Returns undefined if the hex code is not found in the map, indicating that mix-blend-mode: plus-darker should be used.
 *
 * @param hexCode - The hex color code (e.g., '#bfdbfe')
 * @returns A color variant name: 'grey', 'red', 'orange', 'yellow', 'green', 'lime', 'cyan', 'blue', 'indigo', 'purple', or 'magenta', or null if not found
 *
 * @example
 * ```tsx
 * const variant = getAvatarUserFromHex('#bfdbfe') // returns 'blue'
 * const variant = getAvatarUserFromHex('#fecaca') // returns 'red'
 * const variant = getAvatarUserFromHex('#abc123') // returns undefined
 * ```
 */
export function getAvatarUserFromHex(
	hexCode: string,
	number: number = 0
): AvatarProps['color'] {
	/**
	 * Map of hex color codes to avatar color variant names
	 * Based on the design system's avatar-user color values
	 */
	const HEX_TO_VARIANT_MAP: Record<string, AvatarProps['color']> = {
		'#bfdbfe': 'blue', // user-1
		'#c7d2fe': 'indigo', // user-2
		'#d9f99d': 'lime', // user-3
		'#a1eee9': 'cyan', // user-4
		'#f5d0fe': 'magenta', // user-5
		'#ddd6fe': 'purple', // user-6
		'#fed7aa': 'orange', // user-7
		'#fecaca': 'red', // user-8
		'#fef08a': 'yellow', // user-9
		'#bbf7d0': 'green', // user-10
		'#dcdcdc': 'grey', // user-11
		'#dbdde1': 'grey', // user-12 (moon-30, mapped to neutral)
	};

	if (!HEX_TO_VARIANT_MAP[hexCode]) {
		return Object.values(HEX_TO_VARIANT_MAP)[number];
	}

	return HEX_TO_VARIANT_MAP[hexCode];
}

/**
 * Deterministically derives an avatar color variant for a user profile based on
 * their display name. Returns 'blue' for the super-admin and system users.
 */
export const getAvatarColor = (
	profileId: string,
	displayName: string
): AvatarProps['color'] => {
	if (profileId === SUPER_ADMIN_ID || profileId === SYSTEM_USER_ID) {
		return 'blue';
	}
	const charCodeSum = sum(
		displayName.split('').map((_, index) => displayName.charCodeAt(index))
	);
	return getAvatarUserFromHex('', charCodeSum % 6);
};
